import { I18n } from "@shared/services/i18n.service";
import type { TranslationDictionaryType } from "@shared/types/i18n.interface";
import type { KnownLocale } from "@shared/schemas/locale.schemas";
import { resolveI18nDebugMode } from "./i18n-debug.utils";

/**
 * Build a frontend app's i18n instance from the locale set it declares.
 *
 * Each app constructs its own instance rather than sharing one, so an app that
 * declares English only never imports a Spanish dictionary and never ships one.
 * Typing `setLocale` per app falls out of that, but the bundle is the reason:
 * a `declare module` registry (see TranslationKeyRegistry) would pin the types
 * with no build config at all, and still leave every dictionary in every app.
 *
 * `_locales` is unused at runtime and exists only to pin `L`: it is a value
 * rather than a type argument so it cannot be quietly dropped, and because `L`
 * is inferred from it and from `translations` together, the declared set and
 * the supplied dictionaries cannot disagree — an app that declares `en` and
 * `es` must supply both.
 *
 * `primaryLocale` — the locale the app starts on and falls back to — is stated
 * explicitly rather than read off the first declared locale, because host and user pass the
 * shared `SUPPORTED_TRANSLATION_LOCALES`, whose element order is incidental to
 * it being a set; reordering it must not silently change an app's language.
 */
export function createI18n<L extends KnownLocale>(
  _locales: readonly [L, ...L[]],
  primaryLocale: L,
  translations: Record<L, TranslationDictionaryType>,
): I18n<L> {
  return new I18n<L>({
    defaultLocale: primaryLocale,
    fallbackLocale: primaryLocale,
    translations,
    debug: resolveI18nDebugMode(),
  });
}
